import { getPortalFooter } from '@/api/portal'
import { resolveFooterBottomLinks } from '@/components/layout/utils/footerLinkResolver'

const DEFAULT_ICP_URL = 'https://beian.miit.gov.cn/'

function isValidFooterLinkUrl(url) {
  const value = String(url || '').trim()
  return value.startsWith('/') || value.startsWith('http://') || value.startsWith('https://')
}

export function isEmailAddress(url) {
  const value = String(url || '').trim().replace(/^\/+/, '')
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function normalizeCopyright(raw = {}) {
  return {
    text: raw.text ?? ''
  }
}

function normalizeIcp(raw = {}, legacyCopyright = {}) {
  const hasNewFormat = raw && (raw.text != null || raw.url != null)
  if (hasNewFormat) {
    return {
      text: raw.text ?? '',
      url: raw.url ?? ''
    }
  }

  const legacyLabel =
    legacyCopyright.url ??
    legacyCopyright.label ??
    legacyCopyright.icpLabel ??
    legacyCopyright.icpNumber ??
    ''

  return {
    text: legacyLabel,
    url: ''
  }
}

export function getIcpLabel(icp = {}) {
  return normalizeIcp(icp).text
}

export function getIcpHref(icp = {}) {
  const { text, url } = normalizeIcp(icp)
  if (!String(text || '').trim()) return ''

  const trimmedUrl = String(url || '').trim()
  if (trimmedUrl && isValidFooterLinkUrl(trimmedUrl)) return trimmedUrl
  return DEFAULT_ICP_URL
}

let footerFetchSeq = 0
let inflightFooterFetch = null

function pickFooterImageUrl(raw, field) {
  return String(raw?.[field] || '').trim()
}

const emptyFooter = () => ({
  columns: [],
  copyright: { text: '' },
  icp: { text: '', url: '' },
  links: [],
  ctaImageUrl: '',
  footerImageUrl: ''
})

function normalizeFooterData(raw) {
  if (!raw) return emptyFooter()
  return {
    columns: raw.columns || [],
    copyright: normalizeCopyright(raw.copyright),
    icp: normalizeIcp(raw.icp, raw.copyright),
    links: raw.links || [],
    ctaImageUrl: pickFooterImageUrl(raw, 'ctaImageUrl'),
    footerImageUrl: pickFooterImageUrl(raw, 'footerImageUrl')
  }
}

export default {
  namespaced: true,
  state: {
    data: emptyFooter(),
    loading: false,
    loaded: false,
    locale: ''
  },
  getters: {
    columns(state) {
      return state.data.columns || []
    },
    copyright(state) {
      return state.data.copyright || { text: '' }
    },
    icp(state) {
      return state.data.icp || { text: '', url: '' }
    },
    bottomLinks(state, getters, rootState) {
      const menus = rootState.nav?.rawMenus || []
      return resolveFooterBottomLinks(
        state.data.links || [],
        menus,
        rootState.locale?.current
      )
    },
    ctaImageUrl(state) {
      return state.data.ctaImageUrl || ''
    },
    footerImageUrl(state) {
      return state.data.footerImageUrl || ''
    },
    gridClass(state, getters) {
      const extra = getters.bottomLinks.length ? 1 : 0
      const count = Math.max(getters.columns.length, 1) + extra
      if (count >= 5) return 'footer-grid--cols-many'
      return `footer-grid--cols-${Math.max(count, 1)}`
    }
  },
  mutations: {
    SET_FOOTER(state, { data, locale }) {
      state.data = normalizeFooterData(data)
      state.loaded = true
      state.locale = locale || ''
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    RESET(state) {
      state.data = emptyFooter()
      state.loaded = false
      state.locale = ''
      state.loading = false
    }
  },
  actions: {
    async fetchFooter({ commit, state, rootState }, options) {
      const { force = false } = options ?? {}
      const locale = rootState.locale.current
      if (!locale) return

      if (!force && state.loaded && state.locale === locale) return

      if (force) {
        footerFetchSeq += 1
        inflightFooterFetch = null
      }

      if (inflightFooterFetch?.locale === locale && !force) {
        return inflightFooterFetch.promise
      }

      const seq = ++footerFetchSeq
      const task = (async () => {
        commit('SET_LOADING', true)
        try {
          const data = await getPortalFooter(locale, { reload: force })
          if (seq !== footerFetchSeq) return
          commit('SET_FOOTER', { data: data || emptyFooter(), locale })
        } catch (error) {
          if (seq !== footerFetchSeq) return
          commit('SET_FOOTER', { data: emptyFooter(), locale })
          throw error
        } finally {
          if (seq === footerFetchSeq) {
            commit('SET_LOADING', false)
          }
        }
      })()

      inflightFooterFetch = { locale, promise: task }
      try {
        await task
      } finally {
        if (inflightFooterFetch?.promise === task) {
          inflightFooterFetch = null
        }
      }
    }
  }
}
